import React, { useState, useEffect, useRef } from 'react';
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { logout } from '../../store/slices/authSlice';
import { addNotification, removeNotification, clearAllNotifications } from '../../store/slices/notificationSlice';
import {
  HomeIcon, DocumentTextIcon, FolderIcon, PhotoIcon,
  RectangleStackIcon, AcademicCapIcon, BuildingLibraryIcon, EnvelopeIcon,
  DocumentChartBarIcon, Cog6ToothIcon, UserGroupIcon,
  ArrowLeftOnRectangleIcon, Bars3Icon, XMarkIcon, BellIcon, BellAlertIcon, MagnifyingGlassIcon,
  ChevronDownIcon, UserCircleIcon, CloudArrowUpIcon, MegaphoneIcon, ChatBubbleBottomCenterTextIcon,
  CheckCircleIcon, XCircleIcon, ExclamationTriangleIcon, InformationCircleIcon, XMarkIcon as XIcon,
} from '@heroicons/react/24/outline';
import { GlobalSearch } from '../../components/Common';

// Menu items with permission requirements
const allMenuItems = [
  { name: 'Dashboard', path: '/dashboard', icon: HomeIcon, permissions: [] },
  { name: 'Articles', path: '/articles', icon: DocumentTextIcon, permissions: ['VIEW_ARTICLE', 'CREATE_ARTICLE'] },
  { name: 'Categories', path: '/categories', icon: FolderIcon, permissions: ['VIEW_CATEGORY'] },
  { name: 'Hero Content', path: '/hero-content', icon: PhotoIcon, permissions: ['VIEW_HERO_CONTENT', 'MANAGE_HERO_CONTENT'] },
  { name: 'Carousel', path: '/carousel', icon: RectangleStackIcon, permissions: ['VIEW_CAROUSEL'] },
  { name: 'Advertisements', path: '/advertisements', icon: MegaphoneIcon, permissions: ['VIEW_ADVERTISEMENT', 'MANAGE_ADVERTISEMENT'] },
  { name: 'Comments', path: '/comments', icon: ChatBubbleBottomCenterTextIcon, permissions: [] },
  { name: 'Courses', path: '/courses', icon: AcademicCapIcon, permissions: ['VIEW_COURSE'] },
  { name: 'Universities', path: '/universities', icon: BuildingLibraryIcon, permissions: ['VIEW_UNIVERSITY'] },
  { name: 'Contacts', path: '/contacts', icon: EnvelopeIcon, permissions: ['VIEW_CONTACT'] },
  { name: 'Documents', path: '/documents', icon: CloudArrowUpIcon, permissions: ['VIEW_DOCUMENT'] },
  { name: 'Users', path: '/admin/users', icon: UserGroupIcon, permissions: ['VIEW_USER', 'CREATE_USER'], roles: ['ADMIN', 'SUPER_ADMIN'] },
  { name: 'Reports', path: '/reports', icon: DocumentChartBarIcon, permissions: ['VIEW_REPORTS'] },
  { name: 'Settings', path: '/settings', icon: Cog6ToothIcon, permissions: ['VIEW_SETTINGS'] },
];

// Helper function to check if user has permission
const hasPermission = (user, requiredPermissions = [], requiredRoles = []) => {
  if (!user) return false;
  
  // Admin users have all permissions
  if (user.type === 'admin' || user.source === 'admin') return true;
  
  // Parse user permissions
  let userPermissions = [];
  if (Array.isArray(user.permissions)) {
    userPermissions = user.permissions;
  } else if (typeof user.permissions === 'string') {
    userPermissions = user.permissions.split(',').map(p => p.trim()).filter(p => p);
  }
  
  // Check for wildcard permissions
  if (userPermissions.includes('ALL') || userPermissions.includes('*') || userPermissions.includes('ALL_PERMISSIONS')) {
    return true;
  }
  
  // Check role requirements
  if (requiredRoles.length > 0) {
    const userRole = user.primaryRole?.code || user.role;
    const userRoles = user.roles?.map(r => r.code) || [];
    const hasRole = requiredRoles.includes(userRole) || requiredRoles.some(r => userRoles.includes(r));
    if (!hasRole) return false;
  }
  
  // If no specific permissions required, allow access
  if (requiredPermissions.length === 0) return true;
  
  // Check if user has at least one of the required permissions
  return requiredPermissions.some(perm => userPermissions.includes(perm));
};

const Layout = () => {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
  const [profileDropdown, setProfileDropdown] = useState(false);
  const [notificationDropdown, setNotificationDropdown] = useState(false);
  const notificationRef = useRef(null);
  const { user } = useSelector((state) => state.auth);
  const notifications = useSelector((state) => state.notification?.notifications || []);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    setMobileMenuOpen(false);
    setNotificationDropdown(false);
  }, [location.pathname]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (notificationRef.current && !notificationRef.current.contains(event.target)) {
        setNotificationDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const welcomeShown = useRef(false);
  useEffect(() => {
    if (!welcomeShown.current && user) {
      welcomeShown.current = true;
      dispatch(addNotification({
        type: 'info',
        message: `Welcome back, ${user?.firstName || user?.email || 'User'}!`,
        duration: 6000,
      }));
    }
  }, [user, dispatch]);

  const handleLogout = () => {
    dispatch(logout());
    navigate('/login');
  };

  const getInitials = (name) => {
    if (!name) return 'U';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  const userName = user?.firstName ? `${user.firstName} ${user.lastName || ''}`.trim() : user?.email || 'User';
  const userRole = user?.primaryRole?.name || user?.role || 'Admin';

  // Filter menu items based on user permissions
  const menuItems = allMenuItems.filter(item => 
    hasPermission(user, item.permissions || [], item.roles || [])
  );

  return (
    <div className="flex h-screen bg-slate-100">
      {/* Sidebar - Desktop */}
      <aside className={`sidebar hidden lg:flex flex-col transition-all duration-300 ${sidebarOpen ? 'w-64' : 'w-20'}`}>
        {/* Logo */}
        <div className="flex items-center gap-3 p-5 border-b border-white/10">
          <div className="w-10 h-10 bg-gradient-primary rounded-xl flex items-center justify-center">
            <span className="text-white font-bold text-lg">SH</span>
          </div>
          {sidebarOpen && (
            <div className="animate-fadeIn">
              <h1 className="text-white font-bold text-lg">StudentsHerald</h1>
              <p className="text-slate-400 text-xs">Admin Panel</p>
            </div>
          )}
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
          {menuItems.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              className={({ isActive }) =>
                `sidebar-item ${isActive ? 'active' : ''} ${!sidebarOpen ? 'justify-center' : ''}`
              }
              title={!sidebarOpen ? item.name : ''}
            >
              <item.icon className="w-5 h-5 flex-shrink-0" />
              {sidebarOpen && <span className="animate-fadeIn">{item.name}</span>}
            </NavLink>
          ))}
        </nav>

        {/* Sidebar Toggle */}
        <div className="p-4 border-t border-white/10">
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="sidebar-item w-full justify-center"
          >
            <Bars3Icon className="w-5 h-5" />
          </button>
        </div>
      </aside>

      {/* Mobile Sidebar Overlay */}
      {mobileMenuOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="fixed inset-0 bg-black/50" onClick={() => setMobileMenuOpen(false)} />
          <aside className="sidebar fixed left-0 top-0 bottom-0 w-64 animate-slideRight">
            <div className="flex items-center justify-between p-5 border-b border-white/10">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-gradient-primary rounded-xl flex items-center justify-center">
                  <span className="text-white font-bold text-lg">SH</span>
                </div>
                <div>
                  <h1 className="text-white font-bold">StudentsHerald</h1>
                  <p className="text-slate-400 text-xs">Admin Panel</p>
                </div>
              </div>
              <button onClick={() => setMobileMenuOpen(false)} className="text-white p-2">
                <XMarkIcon className="w-6 h-6" />
              </button>
            </div>
            <nav className="p-4 space-y-1">
              {menuItems.map((item) => (
                <NavLink
                  key={item.path}
                  to={item.path}
                  className={({ isActive }) => `sidebar-item ${isActive ? 'active' : ''}`}
                >
                  <item.icon className="w-5 h-5" />
                  <span>{item.name}</span>
                </NavLink>
              ))}
            </nav>
          </aside>
        </div>
      )}

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Header */}
        <header className="bg-white border-b border-slate-200 px-4 lg:px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => setMobileMenuOpen(true)}
                className="lg:hidden p-2 hover:bg-slate-100 rounded-lg"
              >
                <Bars3Icon className="w-6 h-6 text-slate-600" />
              </button>
              
              {/* Mobile Search Button */}
              <button
                onClick={() => setMobileSearchOpen(true)}
                className="md:hidden p-2 hover:bg-slate-100 rounded-lg"
              >
                <MagnifyingGlassIcon className="w-6 h-6 text-slate-600" />
              </button>
              
              {/* Desktop Global Search */}
              <div className="hidden md:block w-80 lg:w-96">
                <GlobalSearch />
              </div>
            </div>

            <div className="flex items-center gap-3">
              {/* Notifications */}
              <div className="relative" ref={notificationRef}>
                <button
                  onClick={() => setNotificationDropdown(!notificationDropdown)}
                  className="relative p-2 hover:bg-slate-100 rounded-xl transition-colors group"
                  title="Notifications"
                >
                  {notifications.length > 0 ? (
                    <BellAlertIcon className="w-6 h-6 text-slate-600" />
                  ) : (
                    <BellIcon className="w-6 h-6 text-slate-600 animate-pulse" />
                  )}
                  {notifications.length > 0 && (
                    <span className="notification-badge">{notifications.length > 9 ? '9+' : notifications.length}</span>
                  )}
                </button>

                {notificationDropdown && (
                  <>
                    <div className="absolute right-0 mt-2 w-80 bg-white rounded-xl shadow-2xl border border-slate-200 z-50 animate-fadeIn">
                      <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
                        <h3 className="font-semibold text-slate-800">Notifications</h3>
                        {notifications.length > 0 && (
                          <button
                            onClick={() => dispatch(clearAllNotifications())}
                            className="text-xs text-indigo-600 hover:text-indigo-800 font-medium"
                          >
                            Clear all
                          </button>
                        )}
                      </div>
                      <div className="max-h-72 overflow-y-auto">
                        {notifications.length === 0 ? (
                          <div className="px-4 py-8 text-center">
                            <BellIcon className="w-10 h-10 mx-auto text-slate-300 mb-2 animate-bounce" />
                            <p className="text-sm font-medium text-slate-600 mb-1">No new notifications</p>
                            <p className="text-xs text-slate-400">You'll see alerts here when actions are performed</p>
                          </div>
                        ) : (
                          [...notifications].reverse().map((n) => {
                            const iconMap = {
                              success: CheckCircleIcon,
                              error: XCircleIcon,
                              warning: ExclamationTriangleIcon,
                              info: InformationCircleIcon,
                            };
                            const colorMap = {
                              success: 'text-emerald-600 bg-emerald-50',
                              error: 'text-red-600 bg-red-50',
                              warning: 'text-amber-600 bg-amber-50',
                              info: 'text-blue-600 bg-blue-50',
                            };
                            const Icon = iconMap[n.type] || InformationCircleIcon;
                            const colors = colorMap[n.type] || colorMap.info;
                            return (
                              <div key={n.id} className="flex items-start gap-3 px-4 py-3 hover:bg-slate-50 border-b border-slate-50 last:border-b-0">
                                <div className={`flex-shrink-0 w-8 h-8 rounded-lg ${colors} flex items-center justify-center`}>
                                  <Icon className="w-4 h-4" />
                                </div>
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm text-slate-700">{n.message}</p>
                                  <p className="text-xs text-slate-400 mt-0.5">
                                    {new Date(n.timestamp).toLocaleTimeString()}
                                  </p>
                                </div>
                                <button
                                  onClick={() => dispatch(removeNotification(n.id))}
                                  className="flex-shrink-0 p-1 hover:bg-slate-200 rounded-full"
                                >
                                  <XIcon className="w-3 h-3 text-slate-400" />
                                </button>
                              </div>
                            );
                          })
                        )}
                      </div>
                    </div>
                    <div className="fixed inset-0 z-40" onClick={() => setNotificationDropdown(false)} />
                  </>
                )}
              </div>

              {/* Profile Dropdown */}
              <div className="relative">
                <button
                  onClick={() => setProfileDropdown(!profileDropdown)}
                  className="flex items-center gap-3 p-2 hover:bg-slate-100 rounded-xl transition-colors"
                >
                  <div className="avatar">{getInitials(userName)}</div>
                  <div className="hidden md:block text-left">
                    <p className="text-sm font-semibold text-slate-800">{userName}</p>
                    <p className="text-xs text-slate-500">{userRole}</p>
                  </div>
                  <ChevronDownIcon className="w-4 h-4 text-slate-400 hidden md:block" />
                </button>

                <div className={`dropdown-menu ${profileDropdown ? 'open' : ''}`}>
                  <div className="p-4 border-b border-slate-100">
                    <p className="font-semibold text-slate-800">{userName}</p>
                    <p className="text-sm text-slate-500">{user?.email}</p>
                  </div>
                  <NavLink to="/settings" className="dropdown-item" onClick={() => setProfileDropdown(false)}>
                    <UserCircleIcon className="w-5 h-5 text-slate-400" />
                    <span>Profile Settings</span>
                  </NavLink>
                  <button onClick={handleLogout} className="dropdown-item w-full text-red-600">
                    <ArrowLeftOnRectangleIcon className="w-5 h-5" />
                    <span>Logout</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 overflow-auto p-4 lg:p-6">
          <Outlet />
        </main>
      </div>

      {/* Click outside to close dropdown */}
      {profileDropdown && (
        <div className="fixed inset-0 z-30" onClick={() => setProfileDropdown(false)} />
      )}

      {/* Mobile Search Modal */}
      {mobileSearchOpen && (
        <div className="fixed inset-0 z-50 md:hidden">
          <div className="fixed inset-0 bg-black/50" onClick={() => setMobileSearchOpen(false)} />
          <div className="fixed top-0 left-0 right-0 bg-white p-4 shadow-lg animate-slideDown">
            <div className="flex items-center gap-3 mb-4">
              <button
                onClick={() => setMobileSearchOpen(false)}
                className="p-2 hover:bg-slate-100 rounded-lg"
              >
                <XMarkIcon className="w-6 h-6 text-slate-600" />
              </button>
              <h3 className="text-lg font-semibold text-slate-800">Search</h3>
            </div>
            <GlobalSearch />
          </div>
        </div>
      )}
    </div>
  );
};

export default Layout;
